import type { BrowserContext } from "@quotientjs/core"
import type { StorageProvider } from "@quotientjs/browser-storage"
import { BrowserContextCookie } from "@/analytics/browser-context-cookie"
import { AnalyticsSession } from "@/analytics/session"
import { Store } from "@/clients/store"
import { DeviceIdStore } from "@/stores/device-id"

/**
 * Owns everything the SDK knows about the current visitor: the analytics
 * session (ID, attribution, referrer), the stored IDs in `Store`, and the
 * `qt_browser_context` cookie that mirrors them for server-side reads. All
 * writes go through here so the cookie can never drift from browser storage:
 * session changes are mirrored by `prepare()`, and `Store` reports its own
 * writes back so a person ID set anywhere is mirrored too.
 */
export class BrowserContextManager {
  /** Stored IDs shared with the other SDK clients. */
  readonly store: Store
  private readonly provider: StorageProvider
  private readonly session: AnalyticsSession
  private readonly cookie: BrowserContextCookie
  // True while prepare() runs, so the Store listener it triggers (adopting a
  // tagged person) doesn't prepare the session a second time.
  private preparing = false

  constructor(
    provider: StorageProvider,
    session: AnalyticsSession = new AnalyticsSession(provider),
    cookie: BrowserContextCookie = new BrowserContextCookie(),
  ) {
    this.provider = provider
    this.session = session
    this.cookie = cookie
    this.store = new Store(provider, {
      personIdChanged: () => {
        // A person can be identified long after the last tracked event (or
        // before any, with page-view tracking off). Bring the session up to
        // date first, otherwise the cookie would republish an expired session
        // and its attribution, or one that was never saved, for another
        // 30 minutes.
        if (!this.preparing) {
          this.session.prepare()
        }
        this.mirror()
      },
      cleared: () => {
        this.cookie.clear()
      },
    })
  }

  /**
   * Brings the session up to date (activity, other tabs, tags on the current
   * URL), adopts a freshly tagged person, and returns the resulting context.
   * Every event build starts here so the payload and the cookie describe the
   * same visitor.
   */
  prepare(): BrowserContext {
    this.preparing = true
    try {
      this.session.prepare()

      // A tagged person ID must remain available after its attribution
      // expires, but a restored (not freshly tagged) attribution shouldn't
      // undo a more authoritative person.upsert() call made since the last
      // page view.
      const taggedPersonId = this.session.attribution.personId
      if (
        taggedPersonId &&
        (this.session.attributionIsFresh || !this.store.personId())
      ) {
        this.store.setPersonId(taggedPersonId)
      }

      return this.mirror()
    } finally {
      this.preparing = false
    }
  }

  private mirror(): BrowserContext {
    const context = this.snapshot()
    this.cookie.write(context)
    return context
  }

  private snapshot(): BrowserContext {
    const deviceId = DeviceIdStore.get(this.provider)
    const personId = this.store.personId()
    const referrerUrl = this.session.referrerUrl
    return {
      version: 1,
      ...(deviceId ? { deviceId } : {}),
      sessionId: this.session.id,
      ...(personId ? { personId } : {}),
      ...(referrerUrl ? { referrerUrl } : {}),
      attribution: this.session.attribution,
    }
  }
}
